import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import ProductCard from "@/components/ProductCard";
import { ProductMedia } from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";
import PageLinkCTA from "@/components/PageLinkCTA";
import { Reveal } from "@/components/Reveal";
import { PRODUCTS, SPEC_NOTE, getProduct } from "@/data/products";

export default function ProductDetail() {
  const { id } = useParams();
  const product = getProduct(id);
  const [modalProduct, setModalProduct] = useState(null);

  if (!product) return <Navigate to="/products" replace />;

  const related = PRODUCTS.filter((p) => p.id !== product.id && p.category === product.category).slice(0, 3);
  const fallbackRelated = PRODUCTS.filter((p) => p.id !== product.id && !p.comingSoon).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  return (
    <>
      <PageHero eyebrow={product.comingSoon ? "Upcoming Product" : product.category} title={product.name} description={product.description} />

      <section className="relative bg-[#F3F7FB] pb-24" data-testid="product-detail-page">
        <div className="wrap relative">
          <Reveal>
            <Link
              to="/products"
              data-testid="product-detail-back-link"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 transition hover:text-brand-emeraldDark"
            >
              <ArrowLeft className="h-4 w-4" /> Back to all products
            </Link>
          </Reveal>

          <div className="mt-10 grid gap-10 lg:grid-cols-2">
            <Reveal className="relative">
              <div className="group overflow-hidden rounded-3xl border border-navy-900/10 shadow-[0_30px_80px_rgba(5,11,24,0.6)]">
                <div className="aspect-[4/3]">
                  <ProductMedia product={product} large />
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12} className="flex flex-col">
              <span className="chip">{product.comingSoon ? "Upcoming" : product.category}</span>
              <h2 className="mt-5 font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-ink sm:text-4xl">
                {product.name}
              </h2>
              <p className="mt-5 leading-relaxed text-slate-600">{product.description}</p>

              <div className="mt-6 rounded-xl border border-navy-900/[0.08] bg-navy-900/[0.03] p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-blueDark">Application</p>
                <p className="mt-2 text-sm text-slate-500">{product.application}</p>
              </div>

              <div className="mt-4 rounded-xl border border-navy-900/[0.08] bg-navy-900/[0.03] p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-blueDark">Specifications</p>
                <p className="mt-2 text-sm text-slate-500">{SPEC_NOTE}</p>
              </div>

              <div className="mt-auto flex flex-wrap gap-4 pt-8">
                <Link
                  to={`/contact?product=${encodeURIComponent(product.name)}`}
                  data-testid="product-detail-enquire-button"
                  className="btn btn-primary"
                >
                  Enquire About This Product
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/products" className="btn btn-outline">
                  Browse Catalogue
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative bg-[#F3F7FB] pb-24" data-testid="product-related">
        <div className="wrap relative">
          <Reveal>
            <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-ink sm:text-3xl">
              Related Products
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {relatedList.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} onOpen={setModalProduct} />
            ))}
          </div>
        </div>
      </section>

      <section className="wrap pb-24">
        <PageLinkCTA />
      </section>

      <ProductModal product={modalProduct} onClose={() => setModalProduct(null)} />
    </>
  );
}
